import {t} from "i18next";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import {apiUrl} from "../../../../api/baseURL";
import centuryGothicBoldTtfUrl from "../../../../fonts/Century Gothic Bold.ttf";
import centuryGothicTtfUrl from "../../../../fonts/Century_Gothic.ttf";
import {cellAdapt} from "../../../../utilities/generalConstants.js/generalConstants";
import {moneyFormat} from "../../../../utilities/moneyFormat";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const COLOR_HEADER_TOP = "#BFBFBF";
const COLOR_MAIN = "#000000";
const COLOR_FOOTER = "#F2F2F2";
const COLOR_DISCOUNT_TEXT = "#EA3323";

const NUMERIC_COLUMN_KEYS = new Set(["price", "discount", "summ", "topDiscount", "totalSumm", "amount", "paymentSumm"]);

const IMAGE_MARGIN = 2;
const IMAGE_CENTER_EXTRA_INSET = 6;
const IMAGE_CELL_INSET = IMAGE_MARGIN * 2 + IMAGE_CENTER_EXTRA_INSET;
const OVERALL_VIEW_IMAGE_FIT_SIZE = 100;
const TABLE_RIGHT_GUTTER = 1;

const commonTableLayout = {
  defaultBorder: true,
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => COLOR_MAIN,
  vLineColor: () => COLOR_MAIN,
  paddingTop: () => 0,
  paddingBottom: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
};

const adjustTableWidths = (columnWidths, pdfWidth) => {
  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
  const scaleFactor = pdfWidth / totalWidth;
  return columnWidths.map(width => Math.floor(width * scaleFactor));
};

const normalizeSingleLineText = value =>
  String(value ?? "")
    .replace(/\s*\n+\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

const getAdaptiveNumberFontSize = (value, {base = 8.5, min = 7} = {}) => {
  const length = normalizeSingleLineText(value).length;

  if (length <= 10) return base;
  if (length <= 12) return Math.max(min, base - 0.5);
  if (length <= 14) return Math.max(min, base - 1);
  if (length <= 16) return Math.max(min, base - 1.5);

  return min;
};

const getServiceLabel = (service, pdfLanguage) => {
  const localeKey = pdfLanguage === "en" ? "nameEng" : "nameUkr";

  return normalizeSingleLineText(service?.type?.[localeKey] || service?.[localeKey] || "");
};

const getServiceValue = service => {
  const value = service?.serviceCost ?? service?.sumAfterDiscount;
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "string" && value.includes("€")) return value;

  return moneyFormat(value);
};

const blobToBase64 = blob => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Font read error"));
    reader.onload = () => {
      const res = typeof reader.result === "string" ? reader.result : "";
      const commaIndex = res.indexOf(",");
      resolve(commaIndex >= 0 ? res.slice(commaIndex + 1) : res);
    };
    reader.readAsDataURL(blob);
  });
};

let centuryGothicInitPromise = null;

const isCenturyGothicReady = () => {
  const hasFontFiles = !!pdfMake?.vfs?.["Century_Gothic.ttf"] && !!pdfMake?.vfs?.["Century_Gothic_Bold.ttf"];
  const centuryFont = pdfMake?.fonts?.CenturyGothic;
  const hasFontMapping =
    !!centuryFont && centuryFont.normal === "Century_Gothic.ttf" && centuryFont.bold === "Century_Gothic_Bold.ttf";

  return hasFontFiles && hasFontMapping;
};

const ensureCenturyGothic = async () => {
  if (isCenturyGothicReady()) return true;

  if (centuryGothicInitPromise) return centuryGothicInitPromise;

  centuryGothicInitPromise = (async () => {
    try {
      const [regularRes, boldRes] = await Promise.all([fetch(centuryGothicTtfUrl), fetch(centuryGothicBoldTtfUrl)]);
      if (!regularRes.ok || !boldRes.ok) return false;

      const [regularBlob, boldBlob] = await Promise.all([regularRes.blob(), boldRes.blob()]);
      pdfMake.vfs = {
        ...(pdfMake.vfs || {}),
        "Century_Gothic.ttf": await blobToBase64(regularBlob),
        "Century_Gothic_Bold.ttf": await blobToBase64(boldBlob),
      };

      const baseFonts = pdfMake.fonts || {};
      const hasRoboto = !!baseFonts?.Roboto;

      pdfMake.fonts = {
        ...baseFonts,
        ...(hasRoboto
          ? {}
          : {
              Roboto: {
                normal: "Roboto-Regular.ttf",
                bold: "Roboto-Medium.ttf",
                italics: "Roboto-Italic.ttf",
                bolditalics: "Roboto-MediumItalic.ttf",
              },
            }),
        CenturyGothic: {
          normal: "Century_Gothic.ttf",
          bold: "Century_Gothic_Bold.ttf",
          //   italics: "Century_Gothic.ttf",
          //   bolditalics: "Century_Gothic_Bold.ttf",
        },
      };

      return isCenturyGothicReady();
    } catch (e) {
      return false;
    } finally {
      centuryGothicInitPromise = null;
    }
  })();

  return centuryGothicInitPromise;
};

const getFontName = async () => {
  const hasCenturyGothic = await ensureCenturyGothic();
  return hasCenturyGothic ? "CenturyGothic" : "Roboto";
};

const buildFooterRow = (colsCount, label, value, {labelBold = false, valueBold = false} = {}) => {
  const row = Array.from({length: colsCount}, () => ({}));
  const normalizedValue = normalizeSingleLineText(value);

  row[0] = {
    text: label,
    colSpan: colsCount - 1,
    style: "footerCell",
    alignment: "right",
    fillColor: COLOR_FOOTER,
    bold: labelBold,
    border: [true, true, true, true],
  };

  for (let i = 1; i < colsCount - 1; i++) {
    row[i] = {
      text: "",
      fillColor: COLOR_FOOTER,
      border: [true, true, true, true],
    };
  }

  row[colsCount - 1] = {
    text: normalizedValue,
    style: "footerCell",
    alignment: "right",
    fillColor: COLOR_FOOTER,
    bold: valueBold,
    noWrap: true,
    lineHeight: 1,
    fontSize: getAdaptiveNumberFontSize(normalizedValue, {base: 9, min: 7}),
    border: [true, true, true, true],
  };

  return row;
};

const buildNoteHeaderRow = (colsCount, text) => {
  const row = Array.from({length: colsCount}, () => ({}));

  row[0] = {
    text,
    colSpan: colsCount,
    style: "noteHeader",
    alignment: "center",
    fillColor: COLOR_HEADER_TOP,
    bold: true,
    border: [true, true, true, true],
  };

  for (let i = 1; i < colsCount; i++) {
    row[i] = {
      text: "",
      fillColor: COLOR_HEADER_TOP,
      border: [true, true, true, true],
    };
  }

  return row;
};

const buildImagesGridNode = (images, {thumbSize = 60, maxPerRow = 8, alignment = "left"} = {}) => {
  if (!Array.isArray(images) || images.length === 0) {
    return {text: ""};
  }

  const rows = [];
  for (let i = 0; i < images.length; i += maxPerRow) {
    rows.push(images.slice(i, i + maxPerRow));
  }

  const widths = rows.length ? rows[0].map(() => thumbSize) : [];

  return {
    table: {
      widths,
      body: rows,
    },
    alignment,
    layout: "noBorders",
  };
};

const buildImageCategoryRow = (
  colsCount,
  label,
  images,
  {fillColor, prefixLines = [], bodyNode = null, showLabel = true} = {},
) => {
  const row = Array.from({length: colsCount}, () => ({}));

  row[0] = {
    text: "",
    fillColor,
    border: [true, true, true, true],
  };

  row[1] = {
    colSpan: colsCount - 1,
    fillColor,
    border: [true, true, true, true],
    stack: [
      ...(showLabel
        ? [
            {
              text: label,
              bold: true,
              fontSize: 8.5,
              margin: [4, 3, 4, 2],
            },
          ]
        : []),
      ...prefixLines,
      bodyNode
        ? bodyNode
        : {
            ...buildImagesGridNode(images, {thumbSize: 60, maxPerRow: 8}),
            margin: [4, 0, 4, 2],
          },
    ],
  };

  for (let i = 2; i < colsCount; i++) {
    row[i] = {
      text: "",
      fillColor,
      border: [true, true, true, true],
    };
  }

  return row;
};

const combineMainImageColumnsForPdf = tableData => {
  const headerKeys = Array.isArray(tableData?.headerKeys) ? tableData.headerKeys : [];
  const image1Index = headerKeys.indexOf("image1");
  const image2Index = headerKeys.indexOf("image2");

  if (image1Index < 0 || image2Index < 0 || image1Index === image2Index) {
    return tableData;
  }

  const normalizeImageCell = value => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    return value ? [value] : [];
  };

  const mergedImageIndex = Math.min(image1Index, image2Index);
  const droppedImageIndex = Math.max(image1Index, image2Index);

  const mergeDataRow = rowData => {
    if (!Array.isArray(rowData)) return rowData;

    const nextRow = [...rowData];
    const mergedImages = [
      ...normalizeImageCell(nextRow[mergedImageIndex]),
      ...normalizeImageCell(nextRow[droppedImageIndex]),
    ];

    nextRow[mergedImageIndex] = mergedImages.length ? mergedImages : "";
    nextRow.splice(droppedImageIndex, 1);

    return nextRow;
  };

  const nextWidths = Array.isArray(tableData?.widths) ? [...tableData.widths] : null;

  if (nextWidths && nextWidths.length > droppedImageIndex) {
    const mergedWidth = Number(nextWidths[mergedImageIndex]) || 0;
    const droppedWidth = Number(nextWidths[droppedImageIndex]) || 0;

    nextWidths[mergedImageIndex] = mergedWidth + droppedWidth;
    nextWidths.splice(droppedImageIndex, 1);
  }

  const nextHeaderRow = Array.isArray(tableData?.headerRow) ? [...tableData.headerRow] : [];
  const mergedHeader = nextHeaderRow[mergedImageIndex] || nextHeaderRow[droppedImageIndex] || "Image";
  nextHeaderRow[mergedImageIndex] = mergedHeader;
  nextHeaderRow.splice(droppedImageIndex, 1);

  const nextHeaderKeys = [...headerKeys];
  nextHeaderKeys[mergedImageIndex] = "image1";
  nextHeaderKeys.splice(droppedImageIndex, 1);

  const nextDataRows = Array.isArray(tableData?.dataRows) ? tableData.dataRows.map(mergeDataRow) : [];

  return {
    ...tableData,
    headerRow: nextHeaderRow,
    headerKeys: nextHeaderKeys,
    dataRows: nextDataRows,
    widths: nextWidths || tableData?.widths,
  };
};

export const generateCofPdf = async ({tableData, format = "A4", pdfLanguage = "en", areVerticalImgColumns = false}) => {
  if (!tableData?.dataRows?.length) return null;

  const pdfTableData = areVerticalImgColumns ? combineMainImageColumnsForPdf(tableData) : tableData;

  const tr = (key, options) => t(key, {lng: pdfLanguage, ...(options || {})});

  const fontName = await getFontName();
  const pageWidth = format === "A3" ? 1190.55 : 841.89;
  const pageMargins = [10, 10, 20, 0];
  const tableNodeRightMargin = TABLE_RIGHT_GUTTER;
  const tableAreaWidth = Math.max(0, pageWidth - pageMargins[0] - pageMargins[2] - tableNodeRightMargin);

  const visibleDataColumnsCount = Array.isArray(pdfTableData?.headerRow) ? pdfTableData.headerRow.length : 0;
  const rawDataWidths = Array.isArray(pdfTableData?.widths) ? pdfTableData.widths : [];

  const normalizedDataWidths = Array.from({length: visibleDataColumnsCount}, (_, index) => {
    const parsed = Number(rawDataWidths[index]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });

  const hasInvalidWidth = normalizedDataWidths.some(width => width === null);
  const sourceDataWidths = hasInvalidWidth
    ? Array.from({length: visibleDataColumnsCount}, () => 1)
    : normalizedDataWidths;

  const dataColumnsTargetWidth = Math.max(0, tableAreaWidth - 10);
  const containerTableWidth = adjustTableWidths(sourceDataWidths, dataColumnsTargetWidth);

  const styles = {
    headerText: {
      fontSize: 9,
      color: COLOR_MAIN,
      margin: [2, 0, 2, 2],
    },
    tableHeader: {
      fontSize: 9,
      color: COLOR_MAIN,
      alignment: "center",
      margin: [1, 2, 1, 2],
    },
    noteHeader: {
      fontSize: 12,
      color: COLOR_MAIN,
      alignment: "center",
      margin: [1, 2, 1, 2],
    },
    cell: {
      fontSize: 8.5,
      color: COLOR_MAIN,
      alignment: "right",
      margin: [1, 2, 1, 2],
    },
    justy: {
      alignment: "center",
    },
    footerCell: {
      fontSize: 9,
      color: COLOR_MAIN,
      margin: [2, 3, 2, 3],
    },
  };

  const imagesDef = {};
  const skuBlockTables = [];
  const footerRows = [];

  const headerKeys = Array.isArray(pdfTableData?.headerKeys) ? pdfTableData.headerKeys : [];
  const discountHeaderIndex = headerKeys.indexOf("discount");
  const topDiscountHeaderIndex = headerKeys.indexOf("topDiscount");

  const redHeaderIndexes = new Set([discountHeaderIndex, topDiscountHeaderIndex].filter(i => i >= 0));
  const redColIndexes = new Set([discountHeaderIndex, topDiscountHeaderIndex].filter(i => i >= 0).map(i => i + 1));
  const numericColIndexes = new Set(
    headerKeys
      .map((headerKey, headerIndex) => (NUMERIC_COLUMN_KEYS.has(headerKey) ? headerIndex + 1 : null))
      .filter(index => index !== null),
  );

  const headerRow = [
    {
      text: "№",
      style: "tableHeader",
      fillColor: COLOR_HEADER_TOP,
      border: [true, true, true, true],
    },
    ...pdfTableData.headerRow.map((th, idx) => ({
      text: th,
      style: "tableHeader",
      color: redHeaderIndexes.has(idx) ? COLOR_DISCOUNT_TEXT : COLOR_MAIN,
      fillColor: COLOR_HEADER_TOP,
      border: [true, true, true, true],
    })),
  ];
  let imgCount = 1;
  let skuIndex = 0;

  const rowImages = Array.isArray(pdfTableData?.rowImages) ? pdfTableData.rowImages : [];
  const headerStringData = Array.isArray(pdfTableData?.headerStringData) ? pdfTableData.headerStringData : [];
  const materialTypes = Array.isArray(pdfTableData?.materialTypes) ? pdfTableData.materialTypes : [];
  const colorTypes = Array.isArray(pdfTableData?.colorTypes) ? pdfTableData.colorTypes : [];

  pdfTableData.dataRows.forEach((rowData, rowIndex) => {
    const skuBlockRows = [];
    const skuLinkedRowIndexes = [];

    const noteText = String(headerStringData[rowIndex] ?? "").trim();
    if (noteText) {
      skuBlockRows.push(buildNoteHeaderRow(headerRow.length, noteText));
    }

    const extraImages = rowImages[rowIndex] || {};
    const overallViewImages = Array.isArray(extraImages.overallView) ? extraImages.overallView.filter(Boolean) : [];
    const finishingImages = Array.isArray(extraImages.decorImages) ? extraImages.decorImages.filter(Boolean) : [];
    const colorImages = Array.isArray(extraImages.colorImages) ? extraImages.colorImages.filter(Boolean) : [];

    const makeImageNodes = (imgs, alignment = "left", fitSize = 60) =>
      imgs.map(img => {
        const node = {
          image: `image${imgCount}`,
          fit: [fitSize, fitSize],
          alignment,
          margin: [IMAGE_MARGIN, IMAGE_MARGIN, IMAGE_MARGIN, IMAGE_MARGIN],
        };
        imagesDef[`image${imgCount}`] = `${apiUrl}/${img}`;
        imgCount++;
        return node;
      });

    if (overallViewImages.length) {
      skuBlockRows.push(
        buildImageCategoryRow(headerRow.length, "", [], {
          fillColor: undefined,
          showLabel: false,
          bodyNode: {
            columns: [
              {width: "*", text: ""},
              {
                width: "auto",
                ...buildImagesGridNode(makeImageNodes(overallViewImages, "center", OVERALL_VIEW_IMAGE_FIT_SIZE), {
                  thumbSize: OVERALL_VIEW_IMAGE_FIT_SIZE,
                  maxPerRow: 8,
                  alignment: "center",
                }),
              },
              {width: "*", text: ""},
            ],
            margin: [4, 3, 4, 2],
          },
        }),
      );
      skuLinkedRowIndexes.push(skuBlockRows.length - 1);
    }

    const tableRow = [];
    tableRow.push({
      text: skuIndex + 1,
      style: "cell",
      fillColor: COLOR_FOOTER,
      border: [true, true, true, true],
      alignment: "right",
    });

    rowData.forEach((cell, cellIndex) => {
      const currentColIndex = cellIndex + 1;
      const isRedCol = redColIndexes.has(currentColIndex);
      const isNumericCol = numericColIndexes.has(currentColIndex);
      const isVwcCol = headerKeys[cellIndex] === "ovh";
      const normalizedCellText = normalizeSingleLineText(cell);
      const adaptiveNumericFontSize = isNumericCol
        ? getAdaptiveNumberFontSize(normalizedCellText, {base: 8.5, min: 7})
        : null;

      if (typeof cell === "object") {
        const imagesCell = [];
        const imgWidth = containerTableWidth[cellIndex];
        const imageFitSize = Math.max(1, imgWidth - IMAGE_CELL_INSET);
        cell.forEach(img => {
          if (img) {
            imagesCell.push({
              image: `image${imgCount}`,
              fit: [imageFitSize, imageFitSize],
              alignment: "center",
              margin: [IMAGE_MARGIN, IMAGE_MARGIN, IMAGE_MARGIN, IMAGE_MARGIN],
            });
            imagesDef[`image${imgCount}`] = `${apiUrl}/${img}`;
            imgCount++;
          }
        });

        tableRow.push({
          style: "justy",
          alignment: "center",
          valign: "middle",
          stack: imagesCell,
          color: isRedCol ? COLOR_DISCOUNT_TEXT : COLOR_MAIN,
          border: [true, true, true, true],
        });
        return;
      }

      if (
        (normalizedCellText?.length > 400 && format === "A4") ||
        (normalizedCellText?.length > 900 && format === "A3")
      ) {
        tableRow.push({
          text: normalizedCellText.slice(0, cellAdapt[format](normalizedCellText.length).sliceTo),
          style: "cell",
          color: isRedCol ? COLOR_DISCOUNT_TEXT : COLOR_MAIN,
          fontSize: isNumericCol ? adaptiveNumericFontSize : cellAdapt[format](normalizedCellText.length).fontSize ?? 6,
          margin: isVwcCol ? [3, 2, 3, 2] : undefined,
          noWrap: false,
          alignment: "right",
          border: [true, true, true, true],
        });
        return;
      }

      tableRow.push({
        text: normalizedCellText,
        style: "cell",
        color: isRedCol ? COLOR_DISCOUNT_TEXT : COLOR_MAIN,
        fontSize: isNumericCol ? adaptiveNumericFontSize : undefined,
        margin: isVwcCol ? [3, 2, 3, 2] : undefined,
        noWrap: false,
        alignment: "right",
        border: [true, true, true, true],
      });
    });

    skuBlockRows.push(tableRow);
    skuLinkedRowIndexes.push(skuBlockRows.length - 1);
    skuIndex++;

    const materialTypeText = String(materialTypes[rowIndex] ?? "").trim();
    const colorTypeText = String(colorTypes[rowIndex] ?? "").trim();

    const finishingNodes = makeImageNodes(finishingImages);
    const colorNodes = makeImageNodes(colorImages);
    const hasFinishingImages = finishingNodes.length > 0;
    const hasColorImages = colorNodes.length > 0;
    const hasFinishingContent = hasFinishingImages || !!materialTypeText;
    const hasColorContent = hasColorImages || !!colorTypeText;

    if (hasFinishingContent || hasColorContent) {
      const bodyNode = {
        margin: [4, 0, 4, 2],
        table: {
          widths: ["auto", "*", "auto", "*"],
          body: [
            [
              {text: hasFinishingContent ? materialTypeText : "", fontSize: 8.5, margin: [0, 2, 8, 0]},
              {
                stack: [
                  ...(hasFinishingContent
                    ? [{text: tr("Finishing"), bold: true, fontSize: 8.5, margin: [0, 0, 0, 2]}]
                    : []),
                  ...(hasFinishingImages ? [buildImagesGridNode(finishingNodes, {thumbSize: 60, maxPerRow: 6})] : []),
                ],
              },
              {text: hasColorContent ? colorTypeText : "", fontSize: 8.5, margin: [8, 2, 8, 0]},
              {
                stack: [
                  ...(hasColorContent ? [{text: tr("Color"), bold: true, fontSize: 8.5, margin: [0, 0, 0, 2]}] : []),
                  ...(hasColorImages ? [buildImagesGridNode(colorNodes, {thumbSize: 60, maxPerRow: 6})] : []),
                ],
              },
            ],
          ],
        },
        layout: "noBorders",
      };

      skuBlockRows.push(
        buildImageCategoryRow(headerRow.length, `${tr("Finishing")} / ${tr("Color")}`, [], {
          fillColor: undefined,
          bodyNode,
        }),
      );
      skuLinkedRowIndexes.push(skuBlockRows.length - 1);
    }

    if (skuLinkedRowIndexes.length > 1) {
      const firstLinkedIndex = skuLinkedRowIndexes[0];
      const lastLinkedIndex = skuLinkedRowIndexes[skuLinkedRowIndexes.length - 1];

      skuLinkedRowIndexes.forEach(linkedRowIndex => {
        const firstCell = skuBlockRows[linkedRowIndex]?.[0];
        if (!firstCell) return;

        firstCell.fillColor = COLOR_FOOTER;
        firstCell.border = [true, linkedRowIndex === firstLinkedIndex, true, linkedRowIndex === lastLinkedIndex];
      });
    }

    skuBlockTables.push({
      margin: [0, 0, tableNodeRightMargin, 0],
      unbreakable: true,
      table: {
        widths: [10, ...containerTableWidth],
        body: skuBlockRows,
        dontBreakRows: true,
      },
      layout: commonTableLayout,
    });
  });

  const servicesAndDiscounts = tableData?.servicesAndDiscounts || {};
  const hiddenRows = Array.isArray(servicesAndDiscounts.hiddenRows) ? servicesAndDiscounts.hiddenRows : [];
  const hasDiscount = !!servicesAndDiscounts.discount;
  const services = Array.isArray(servicesAndDiscounts.services) ? servicesAndDiscounts.services : [];

  const footerCandidates = [
    {
      key: "resultSumWithoutDiscount",
      label: tr("SKU sum without discount"),
      value: servicesAndDiscounts.resultSumWithoutDiscount,
      enabled: hasDiscount && !hiddenRows.includes("resultSumWithoutDiscount"),
      bold: false,
    },
    {
      key: "discountSum",
      label: tr("Discount sum"),
      value: servicesAndDiscounts.discountSum,
      enabled: hasDiscount && !hiddenRows.includes("discountSum"),
      bold: false,
    },
    {
      key: "totalSumWithDiscount",
      label: tr("Amount by SKU"),
      value: servicesAndDiscounts.totalSumWithDiscount || servicesAndDiscounts.resultSumWithoutDiscount,
      enabled: !hiddenRows.includes("totalSumWithDiscount"),
      bold: false,
    },
    {
      key: "resultSum",
      label: tr("Total amount to pay"),
      value: servicesAndDiscounts.resultSum,
      enabled: !hiddenRows.includes("resultSum"),
      bold: true,
    },
    {
      key: "resultAdvanceSum",
      label: tr("Advance amount to pay 70"),
      value: servicesAndDiscounts.resultAdvanceSum,
      enabled: !hiddenRows.includes("resultAdvanceSum"),
      bold: false,
    },
  ];

  const serviceFooterCandidates = !hiddenRows.includes("services")
    ? services
        .map(service => {
          const label = getServiceLabel(service, pdfLanguage);
          const value = getServiceValue(service);

          return {
            key: `service-${service?.serviceId ?? label}`,
            label,
            value,
            enabled: !!label && !!value,
            bold: false,
          };
        })
        .filter(item => item.enabled)
    : [];

  const resultSumIndex = footerCandidates.findIndex(item => item.key === "resultSum");
  if (serviceFooterCandidates.length) {
    if (resultSumIndex >= 0) {
      footerCandidates.splice(resultSumIndex + 1, 0, ...serviceFooterCandidates);
    } else {
      footerCandidates.push(...serviceFooterCandidates);
    }
  }

  const visibleFooterCandidates = footerCandidates
    .filter(r => r.enabled)
    .filter(r => r.value !== undefined && r.value !== null && String(r.value) !== "");

  const visibleResultSumIndex = visibleFooterCandidates.findIndex(item => item.key === "resultSum");

  if (visibleResultSumIndex > -1) {
    const [resultSumRow] = visibleFooterCandidates.splice(visibleResultSumIndex, 1);
    const resultAdvanceIndex = visibleFooterCandidates.findIndex(item => item.key === "resultAdvanceSum");

    if (resultAdvanceIndex > -1) {
      visibleFooterCandidates.splice(resultAdvanceIndex, 0, resultSumRow);
    } else {
      visibleFooterCandidates.push(resultSumRow);
    }
  }

  visibleFooterCandidates.forEach(({label, value, bold}) => {
    footerRows.push(
      buildFooterRow(headerRow.length, label, value, {
        labelBold: bold,
        valueBold: bold,
      }),
    );
  });

  const headerNameNode =
    typeof tableData.text === "string"
      ? {text: tableData.text, style: "headerText"}
      : {
          margin: [0, 0, 0, 4],
          table: {
            widths: ["*"],
            body: [[{...(tableData.text || {}), style: "headerText", border: [true, true, true, true]}]],
          },
          layout: {
            defaultBorder: true,
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => COLOR_MAIN,
            vLineColor: () => COLOR_MAIN,
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 2,
            paddingBottom: () => 2,
          },
        };

  const documentDefinition = {
    defaultStyle: {
      font: fontName,
    },
    pageSize: format,
    pageOrientation: "landscape",
    pageMargins: [10, 10, 20, 0],
    content: [
      ...(Array.isArray(tableData.header) ? tableData.header : []),
      headerNameNode,
      {
        margin: [0, 0, tableNodeRightMargin, 0],
        table: {
          widths: [10, ...containerTableWidth],
          body: [headerRow],
          dontBreakRows: true,
        },
        layout: {
          ...commonTableLayout,
          hLineColor: i => (i === 0 ? COLOR_HEADER_TOP : COLOR_MAIN),
        },
      },
      ...skuBlockTables,
      ...(footerRows.length
        ? [
            {
              margin: [0, 0, tableNodeRightMargin, 0],
              unbreakable: true,
              table: {
                widths: [10, ...containerTableWidth],
                body: footerRows,
                dontBreakRows: true,
              },
              layout: {
                defaultBorder: true,
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => COLOR_MAIN,
                vLineColor: () => COLOR_MAIN,
                paddingTop: () => 0,
                paddingBottom: () => 0,
                paddingLeft: () => 0,
                paddingRight: () => 0,
              },
            },
          ]
        : []),
    ],
    images: {...imagesDef},
    styles,
  };

  try {
    return pdfMake.createPdf(documentDefinition);
  } catch (error) {
    const fallbackDocumentDefinition = {
      ...documentDefinition,
      defaultStyle: {
        ...(documentDefinition.defaultStyle || {}),
        font: "Roboto",
      },
    };

    return pdfMake.createPdf(fallbackDocumentDefinition);
  }
};

export default generateCofPdf;
